import type { IComment } from 'api/bitedb/comment/comment';
import type { IUser } from 'interfaces/IUser';
import React from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

interface CommentsProps {
  comments: Partial<IComment>[];
  isLoadingComments: boolean;
  commentContent: string;
  setCommentContent: (content: string) => void;
  handleAddComment: () => void;
  handleDeleteComment: (commentId: string) => void;
  cur_user: IUser | null;
  getDateDifference: (dateString: string) => string;
  appearance?: 'default' | 'entity-hub';
}

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?';

const Comments: React.FC<CommentsProps> = ({
  comments,
  isLoadingComments,
  commentContent,
  setCommentContent,
  handleAddComment,
  handleDeleteComment,
  cur_user,
  getDateDifference,
  appearance = 'default',
}) => {
  if (appearance === 'entity-hub') {
    const currentUserName = [
      cur_user?.basics?.firstName,
      cur_user?.basics?.lastName,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className='entity-hub-comments'>
        <div className='entity-hub-comment-composer'>
          <span className='entity-hub-comment-avatar current-user'>
            {getInitials(currentUserName || 'You')}
          </span>
          <div className='entity-hub-comment-composer-main'>
            <textarea
              value={commentContent}
              onChange={(event) => setCommentContent(event.target.value)}
              placeholder='Leave a note on this record...'
              aria-label='Comment on this record'
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  if (commentContent.trim()) handleAddComment();
                }
              }}
            />
            <div className='entity-hub-comment-composer-footer'>
              <span>Comments are visible to everyone on your team.</span>
              <button
                type='button'
                onClick={handleAddComment}
                disabled={!commentContent.trim()}
              >
                Add comment
              </button>
            </div>
          </div>
        </div>

        <div className='entity-hub-comment-feed'>
          {isLoadingComments ? (
            <div className='entity-hub-drawer-state'>Loading comments...</div>
          ) : !Array.isArray(comments) || comments.length === 0 ? (
            <div className='entity-hub-drawer-state'>
              No comments yet. Leave the first note on this record.
            </div>
          ) : (
            [...comments].reverse().map((comment) => {
              const authorName = comment.author_name || 'Anonymous';
              const canDelete =
                cur_user?.basics?.id === comment.author_id || cur_user?.isAdmin;

              return (
                <article className='entity-hub-comment-item' key={comment.id}>
                  <span className='entity-hub-comment-avatar'>
                    {getInitials(authorName)}
                  </span>
                  <div>
                    <div className='entity-hub-comment-meta'>
                      <strong>{authorName}</strong>
                      <time>
                        {comment.created_at
                          ? getDateDifference(comment.created_at)
                          : 'Just now'}
                      </time>
                      {canDelete ? (
                        <button
                          type='button'
                          aria-label={`Delete comment by ${authorName}`}
                          onClick={() => handleDeleteComment(comment.id ?? '')}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                        </button>
                      ) : null}
                    </div>
                    <p>{comment.content}</p>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='relationship-tab-panel'>
      <div className='comment-container relationship-comment-container'>
        <h3 className='screening-ai-label-text'>
          Comments ({Array.isArray(comments) ? comments.length : 0})
        </h3>

        <div style={{ flexShrink: 0 }}>
          <textarea
            value={commentContent}
            onChange={(e) => setCommentContent(e.target.value)}
            placeholder='Comment...'
            className='comments-panel-footer-cont-action-input-cont'
            style={{
              width: '100%',
              height: '80px',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddComment();
              }
            }}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '8px',
            }}
          >
            <span className='comments-panel-body-section-feed-cont-msg-text'>
              Press Enter to submit
            </span>
            <button
              onClick={handleAddComment}
              disabled={!commentContent.trim()}
              style={{
                backgroundColor: commentContent.trim() ? '#4A90E2' : '#2E3036',
                color: commentContent.trim() ? '#FFFFFF' : '#8A8D93',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '14px',
                fontFamily: 'Inter',
                cursor: commentContent.trim() ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease',
              }}
            >
              Add Comment
            </button>
          </div>
        </div>

        <div style={{ margin: '4px 0' }}></div>

        <div className='comment-container-top'>
          {isLoadingComments ? (
            <div className='hit-meta' style={{ textAlign: 'center' }}>
              Loading comments...
            </div>
          ) : !Array.isArray(comments) || comments.length === 0 ? (
            <div className='hit-meta' style={{ textAlign: 'center' }}>
              No comments yet.
            </div>
          ) : (
            [...comments]
              .map((comment) => (
                <div
                  key={comment.id}
                  style={{
                    padding: '0px 16px',
                    borderBottom: '1px solid #2E3036',
                  }}
                >
                  <div className='comments-panel-body-section-feed-cont-text-sec'>
                    <h2
                      className='hit-title'
                      style={{ color: 'rgb(72, 149, 239)' }}
                    >
                      {comment.author_name || 'Anonymous'}
                    </h2>
                    <p>
                      {comment.created_at
                        ? getDateDifference(comment.created_at)
                        : 'Just now'}
                    </p>
                    {(cur_user?.basics?.id === comment.author_id ||
                      cur_user?.isAdmin) && (
                      <div>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          onClick={() => {
                            handleDeleteComment(comment.id ?? '');
                          }}
                          style={{ cursor: 'pointer' }}
                          width='20'
                          height='20'
                          viewBox='0 0 20 20'
                          fill='none'
                        >
                          <path
                            d='M7.5 2.5H12.5M2.5 5H17.5M15.8333 5L15.2489 13.7661C15.1612 15.0813 15.1174 15.7389 14.8333 16.2375C14.5833 16.6765 14.206 17.0294 13.7514 17.2497C13.235 17.5 12.5759 17.5 11.2578 17.5H8.74221C7.42409 17.5 6.76503 17.5 6.24861 17.2497C5.79396 17.0294 5.41674 16.6765 5.16665 16.2375C4.88259 15.7389 4.83875 15.0813 4.75107 13.7661L4.16667 5M8.33333 8.75V12.9167M11.6667 8.75V12.9167'
                            stroke='#BDBEC1'
                            strokeWidth='1.66667'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          ></path>
                        </svg>
                      </div>
                    )}
                  </div>
                  <p className='screening-ai-sum-result-text'>
                    {comment.content}
                  </p>
                </div>
              ))
              .reverse()
          )}
        </div>
      </div>
    </div>
  );
};

export default Comments;
